import { useState } from "react";
import type { Account, BillablePurchaseOrder, SupplierOption, VendorBill } from "../../services/financialService";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import AccountSelect from "./AccountSelect";

/**
 * A supplier bill's own fields. Supplier and amount are kept in state
 * because the description's assistant reads them (with the order) to know
 * what it is describing. Mount with a `key` per bill so an edit starts from
 * that bill's values.
 */
export function VendorBillFields({
  editing,
  suppliers,
  purchaseOrders,
  accounts,
  orderId,
  onOrderChange,
  chargeable,
  canHoldPayable,
  today,
}: {
  editing?: VendorBill;
  suppliers: SupplierOption[];
  purchaseOrders: BillablePurchaseOrder[];
  accounts?: Account[];
  orderId: string;
  onOrderChange: (orderId: string) => void;
  chargeable: (account: Account) => boolean;
  canHoldPayable: (account: Account) => boolean;
  today: string;
}) {
  const [supplier, setSupplier] = useState(editing ? String(editing.supplier) : "");
  const [amount, setAmount] = useState(editing?.total_amount ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");

  return (
    <>
      <FormSection title="Supplier and invoice" divided={false} columns={4}>
        <Field label="Supplier">
          <select required name="supplier" className="input mt-1" value={supplier} onChange={(event) => setSupplier(event.target.value)}>
            <option value="">Choose a supplier</option>
            {suppliers.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
          </select>
        </Field>
        <Field label="Their invoice number">
          <input required name="number" className="input mt-1" placeholder="As printed on the invoice" defaultValue={editing?.bill_number ?? ""} />
        </Field>
        <Field label="Invoice date">
          <input required type="date" name="date" max={today} className="input mt-1" defaultValue={editing?.bill_date ?? ""} />
        </Field>
        <Field label="Due date">
          <input required type="date" name="due" className="input mt-1" defaultValue={editing?.due_date ?? ""} />
        </Field>
      </FormSection>

      <FormSection title="What is being paid for" columns={4}>
        <Field label="Purchase order" optional className="sm:col-span-2" hint="Link the order to check the bill against what was received.">
          <select name="purchase_order" className="input mt-1" value={orderId} onChange={(event) => onOrderChange(event.target.value)}>
            <option value="">No purchase order (a service, or an unordered buy)</option>
            {purchaseOrders.map((order) => (
              <option key={order.id} value={order.id}>{order.order_number} · {order.supplier_name} · UGX {Number(order.total_amount).toLocaleString()}</option>
            ))}
          </select>
        </Field>
        <Field label="Amount (UGX)" className="sm:col-span-2 lg:col-span-1">
          <input
            required
            min="0.01"
            step="0.01"
            type="number"
            name="amount"
            className="input mt-1 tabular-nums"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
        <Field as="div" label="Description" className="sm:col-span-2 lg:col-span-4" hint="What was supplied or done, for the reviewer, approver and auditors.">
          <AIAssistField
            name="description"
            rows={2}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="e.g. 20 reams of A4 paper and 5 printer toners for the exams office"
            context={{
              key: "financial.bill_description",
              params: { supplier: supplier || null, purchase_order: orderId || null, amount: amount || null, bill: editing?.id ?? null },
            }}
            draftLabel="Draft description"
            draftHint={supplier ? "Reads the supplier and any order. Jot a few words first for a better draft." : "Choose the supplier first, then draft."}
          />
        </Field>
        <Field as="div" label="Charge to" className="sm:col-span-2" hint="The expense, or the asset being capitalised.">
          <AccountSelect
            required
            name="account"
            placeholder="Choose an account"
            accounts={accounts}
            isSelectable={chargeable}
            defaultValue={editing?.expense_account ? String(editing.expense_account) : ""}
          />
        </Field>
        <Field as="div" label="Supplier's payable account" optional className="sm:col-span-2" hint="Left alone, the supplier's own account is used.">
          <AccountSelect
            name="payable_account"
            placeholder="Automatic"
            accounts={accounts}
            isSelectable={canHoldPayable}
            defaultValue={editing?.payable_account ? String(editing.payable_account) : ""}
          />
        </Field>
      </FormSection>
    </>
  );
}
